import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createContractComment,
  deleteContractComment,
  downloadContractCommentAttachment,
  fetchContractComments,
} from '../../api/contractComment';
import { ContractComment } from '../../types/contractComment';
import './ContractComments.css';

import { reportError } from '../../utils/notify';
interface ContractCommentsProps {
  contractId?: number | null;
  /** 있으면 해당 계약 단계 메모, 없으면 계약 공통 댓글 */
  stageId?: number | null;
  /** false면 조회/작성 모두 비활성 (신규 등록 전) */
  enabled?: boolean;
  readOnly?: boolean;
  title?: string;
  subtitle?: string;
}

function ContractComments({
  contractId,
  stageId = null,
  enabled = true,
  readOnly = false,
  title = '비고 · 댓글',
  subtitle = '진행 메모와 첨부파일을 남길 수 있습니다',
}: ContractCommentsProps) {
  const canUse = Boolean(enabled && contractId != null);
  const [comments, setComments] = useState<ContractComment[]>([]);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!canUse || contractId == null) {
      setComments([]);
      return;
    }
    try {
      setLoading(true);
      const list = await fetchContractComments(contractId, stageId);
      setComments(list);
      setError('');
    } catch {
      reportError(setError, '댓글을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [canUse, contractId, stageId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments]);

  const handleSubmit = async () => {
    if (!canUse || contractId == null || readOnly) return;
    if (!content.trim() && files.length === 0) {
      reportError(setError, '내용 또는 첨부파일을 추가해 주세요.');
      return;
    }
    try {
      setSubmitting(true);
      await createContractComment(contractId, content, files, stageId);
      setContent('');
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setError('');
      await load();
    } catch {
      reportError(setError, '댓글 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment: ContractComment) => {
    if (!canUse || contractId == null || !comment.canDelete) return;
    const ok = window.confirm('이 댓글을 삭제할까요?');
    if (!ok) return;
    try {
      await deleteContractComment(contractId, comment.commentId);
      await load();
    } catch {
      reportError(setError, '댓글 삭제에 실패했습니다.');
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <section className="contract-comments">
      <div className="contract-comments__head">
        <div>
          <h2>{title}</h2>
          <span>{subtitle}</span>
        </div>
      </div>

      {!canUse ? (
        <p className="contract-comments__hint">
          계약서를 먼저 임시저장 또는 등록하면 댓글과 첨부파일을 작성할 수 있습니다.
        </p>
      ) : (
        <>
          <div className="contract-comments__list" ref={listRef}>
            {loading ? (
              <p className="contract-comments__hint">불러오는 중...</p>
            ) : comments.length === 0 ? (
              <p className="contract-comments__hint">
                아직 댓글이 없습니다. 예: &quot;오늘은 계약담당자와 미팅 다녀옴&quot; + 카탈로그 첨부
              </p>
            ) : (
              comments.map((comment) => (
                <article key={comment.commentId} className="contract-comments__item">
                  <header>
                    <strong>{comment.createdUserName || '사용자'}</strong>
                    <time>{formatDateTime(comment.createdAt)}</time>
                    {comment.canDelete && !readOnly && (
                      <button
                        type="button"
                        className="contract-comments__delete"
                        onClick={() => void handleDelete(comment)}
                      >
                        삭제
                      </button>
                    )}
                  </header>
                  <p className="contract-comments__content">{comment.content}</p>
                  {comment.attachments && comment.attachments.length > 0 && (
                    <ul className="contract-comments__files">
                      {comment.attachments.map((file) => (
                        <li key={file.attachmentId}>
                          <button
                            type="button"
                            onClick={() =>
                              void downloadContractCommentAttachment(
                                contractId!,
                                comment.commentId,
                                file.attachmentId,
                                file.fileName
                              )
                            }
                          >
                            📎 {file.fileName}
                            {file.fileSize != null ? ` (${formatSize(file.fileSize)})` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))
            )}
          </div>

          {!readOnly && (
            <div className="contract-comments__composer">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder="예: 오늘은 계약담당자와 미팅 다녀옴"
              />
              {files.length > 0 && (
                <ul className="contract-comments__pending">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`}>
                      <span>
                        {file.name} ({formatSize(file.size)})
                      </span>
                      <button type="button" onClick={() => removeFile(index)}>
                        제거
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="contract-comments__composer-actions">
                <label className="contract-comments__attach">
                  첨부파일
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => {
                      const next = Array.from(e.target.files ?? []);
                      setFiles((prev) => [...prev, ...next].slice(0, 10));
                      e.target.value = '';
                    }}
                  />
                </label>
                <button type="button" disabled={submitting} onClick={() => void handleSubmit()}>
                  {submitting ? '등록 중...' : '댓글 등록'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {error && <p className="contract-comments__error">{error}</p>}
    </section>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSize(size: number) {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

export default ContractComments;
